/**
 * Page max-width override — lets a route ask AppShell for a different
 * canvas width than its static PAGE_MAX_WIDTHS entry. Used by pages
 * whose ideal width depends on runtime state (e.g., ContactsPage:
 * max-w-7xl for the common 5-column case, max-w-screen-2xl when the
 * Line + Client demo columns are visible for admin / Multi-WA Pro /
 * Agency tier).
 *
 * AppShell provides the context + holds the override state. Pages
 * consume the hook in render — it sets the override on mount + change,
 * and clears it on unmount so the next route gets a fresh slate.
 *
 * The banner block in AppShell (EmailVerificationBanner) reads the
 * same effective width — single source of
 * truth so banners stay aligned with the page content beneath them
 * (locked in Phase 8).
 */

import { createContext, useContext, useEffect } from 'react';

export interface PageLayoutValue {
  /**
   * Override the static PAGE_MAX_WIDTHS entry for the current route.
   * Pass `null` (or call `clear` via useEffect cleanup) to restore.
   */
  setOverride: (className: string | null) => void;
}

export const PageLayoutContext = createContext<PageLayoutValue>({
  setOverride: () => {
    /* no-op when outside Provider — Storybook / tests */
  },
});

/**
 * Page-side hook. Sets the override on mount + whenever `className`
 * changes; clears it on unmount.
 *
 * There's a one-render flash on initial mount: the page renders inside
 * the static AppShell max-w, then this effect fires and AppShell
 * re-renders with the override. For most pages this is imperceptible
 * (content beats the banner block in to the DOM). Acceptable.
 */
export function usePageMaxWidth(className: string | null): void {
  const { setOverride } = useContext(PageLayoutContext);
  useEffect(() => {
    setOverride(className);
    return () => setOverride(null);
  }, [className, setOverride]);
}
